import { useSyncExternalStore, useDeferredValue } from 'react';
import { IWindowSize, sharedWindowSizeHandlers } from '@honeybook/hbui';
import { useRouter } from 'next/router';

type WindowSizePayload = ReturnType<(typeof sharedWindowSizeHandlers)['getPayload']>;
const isClient = typeof window === 'object';

/*
The functions in this file expose windowSize helper hooks that are SSR and hydration safe,
as well as leveraging built in react 18 hooks to create suspense safe implementations of these
hooks by using useSyncExternalStore and useDeferredValue

For more info on why we're using both hooks in this way:
// https://kurtextrem.de/posts/react-uses-hydration
 */

// are we in desktop mode?
export function useIsDesktop(): boolean {
    const { query } = useRouter();
    const handlers = getHandlers(Number(query.width));
    const isDesktop = useSyncExternalStore<boolean>(handlers.subscribe, getValue, getSSRSnapshot);

    return useDeferredValue(isDesktop);

    function getValue() {
        const payload = handlers.getValue();
        return payload.isMediumUp;
    }

    function getSSRSnapshot() {
        if (!query.width) {
            // in case we're missing width info in ssr, default to `true` since that is our core experience
            return true;
        }
        const payload = handlers.getServerSnapshot();
        return payload.isMediumUp;
    }
}

// are we in mobile mode?
export function useIsMobile(): boolean {
    const { query } = useRouter();
    const handlers = getHandlers(Number(query.width));

    const isMobile = useSyncExternalStore<boolean>(handlers.subscribe, getValue, getSSRSnapshot);

    return useDeferredValue(isMobile);

    function getValue() {
        const payload = handlers.getValue();
        return !payload.isMediumUp;
    }

    function getSSRSnapshot() {
        if (!query.width) {
            // in case we're missing width info in ssr, default to `false` since our default experience
            // is desktop
            return false;
        }
        const payload = handlers.getServerSnapshot();
        return !payload.isMediumUp;
    }
}

// This function provides a hydration-safe implementation of useSharedWindowSize that allows for accessing
// windowSize data on the server when applicable.
// on the client it used the same infra as useSharedWindowSize so there is no extra listeners
export function useSafeWindowSize(): WindowSizePayload {
    const { query } = useRouter();
    const handlers = getHandlers(Number(query.width));

    return useDeferredValue(useSyncExternalStore<WindowSizePayload>(handlers.subscribe, handlers.getValue, handlers.getServerSnapshot));
}

function getHandlers(width: number) {
    const originalSize = isClient ? sharedWindowSizeHandlers.getSize(isClient) : { width, height: 10 };

    let payload: WindowSizePayload = sharedWindowSizeHandlers.getPayload(originalSize);

    return {
        subscribe: (cb: () => void) => {
            // safe to call on every subscribe because the method self-checks
            sharedWindowSizeHandlers.startListening();

            sharedWindowSizeHandlers.addListener(listener);

            return () => {
                sharedWindowSizeHandlers.removeListener(listener);
            };
            function listener(size: IWindowSize) {
                if (size.width != payload.windowSize.width) {
                    payload = sharedWindowSizeHandlers.getPayload(size);
                    cb();
                }
            }
        },
        getValue: () => {
            return payload;
        },
        getServerSnapshot: () => {
            return payload;
        }
    };
}
